import React from 'react';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid';

import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import { FGrid, FGridMoneyCell } from '../grid';
import { FGridCodeCombo } from '../grid/FGridCodeCombo';
import { makeTempRowId } from '../grid/FGridUtils';

type Props = {
  rows: any[];
  loading?: boolean;
  selectedTask: any | null;
  accounts?: any[];
  onChange: (rows: any[]) => void;
  onSubmit: (rows: any[]) => Promise<void>;
};

type State = {
  rowModesModel: GridRowModesModel;
  saving: boolean;
};

class BudgetLineGridPanel extends React.Component<Props, State> {
  state: State = {
    rowModesModel: {},
    saving: false,
  };

  getRowId = (row: any) => row.id || row.rid || row.budgetlineid;

  addRow = () => {
    const { selectedTask } = this.props;
    if (!selectedTask) return;

    const id = makeTempRowId('new-budgetline');
    const taskid = selectedTask.taskid || selectedTask.rid || selectedTask.id;

    const newRow = {
      id,
      rid: null,
      budgetlineid: null,
      taskid,
      accountid: null,
      accountcode: '',
      accountname: '',
      description: '',
      amount: 0,
      status: 1,
      _state: 'new',
    };

    this.props.onChange([...(this.props.rows || []), newRow]);

    setTimeout(() => {
      this.setState({
        rowModesModel: {
          ...this.state.rowModesModel,
          [id]: { mode: GridRowModes.Edit },
        },
      });
    }, 0);
  };

  deleteRow = (id: any) => {
    const rows = (this.props.rows || [])
      .map((r: any) => {
        const key = this.getRowId(r);
        if (String(key) !== String(id)) return r;

        if (String(r.id || '').startsWith('new-')) {
          return { ...r, _state: 'discard' };
        }

        return { ...r, _state: 'deleted', status: 0 };
      })
      .filter((r: any) => r._state !== 'discard');

    this.props.onChange(rows);
  };

  handleEditClick = (id: any) => {
    this.setState({
      rowModesModel: {
        ...this.state.rowModesModel,
        [id]: { mode: GridRowModes.Edit },
      },
    });
  };

  handleSaveClick = (id: any) => {
    this.setState({
      rowModesModel: {
        ...this.state.rowModesModel,
        [id]: { mode: GridRowModes.View },
      },
    });
  };

  handleCancelClick = (id: any) => {
    this.setState({
      rowModesModel: {
        ...this.state.rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      },
    });
  };

  processRowUpdate = (newRow: GridRowModel) => {
    const newId = this.getRowId(newRow);

    const rows = (this.props.rows || []).map((r: any) =>
      String(this.getRowId(r)) === String(newId)
        ? {
            ...r,
            ...newRow,
            id: r.id || newRow.id || newId,
            _state: (r.rid || r.budgetlineid) ? 'dirty' : 'new',
          }
        : r
    );

    this.props.onChange(rows);
    return rows.find((r: any) => String(this.getRowId(r)) === String(newId)) || newRow;
  };

  saveAll = async () => {
    this.setState({ saving: true });

    try {
      await this.props.onSubmit(this.props.rows || []);
    } finally {
      this.setState({ saving: false });
    }
  };

  renderAccountEditor = (params: any) => (
    <FGridCodeCombo
      label="Account"
      items={this.props.accounts || []}
      value={params.value || ''}
      type="CODE"
      autoOpen
      {...params}
    />
  );

  gridActions = (params: any) => {
    const isEditing = this.state.rowModesModel[params.id]?.mode === GridRowModes.Edit;

    if (isEditing) {
      return [
        <GridActionsCellItem
          key="save"
          icon={<SaveIcon />}
          label="Save"
          onClick={() => this.handleSaveClick(params.id)}
        />,
        <GridActionsCellItem
          key="cancel"
          icon={<CancelIcon />}
          label="Cancel"
          onClick={() => this.handleCancelClick(params.id)}
        />,
      ];
    }

    return [
      <GridActionsCellItem
        key="edit"
        icon={<EditIcon />}
        label="Edit"
        onClick={() => this.handleEditClick(params.id)}
      />,
      <GridActionsCellItem
        key="delete"
        icon={<DeleteIcon />}
        label="Delete"
        onClick={() => this.deleteRow(params.id)}
      />,
    ];
  };

  getColumns = (): GridColDef[] => [
    {
      field: 'accountcode',
      headerName: 'Account',
      width: 160,
      editable: true,
      renderEditCell: this.renderAccountEditor,
    },
    { field: 'accountname', headerName: 'Account Name', flex: 1, minWidth: 260 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 260, editable: true },
    { field: 'amount', headerName: 'Amount', width: 160, type: 'number', editable: true, renderCell: FGridMoneyCell },
    { field: 'status', headerName: 'Status', width: 90 },
    { field: 'actions', type: 'actions', width: 100, getActions: this.gridActions },
  ];

  render() {
    const { rows, loading, selectedTask } = this.props;

    return (
      <FGrid
        title="Budget Lines"
        subtitle={`Budget lines for task: ${selectedTask?.taskcode || selectedTask?.taskname || ''}`}
        rows={rows || []}
        columns={this.getColumns()}
        loading={loading || this.state.saving}
        height={360}
        addRow={this.addRow}
        saveAll={this.saveAll}
        showQuickFilter
        toolbarSx={{ backgroundColor: '#e7f6ea' }}
        getRowId={this.getRowId}
        editMode="row"
        rowModesModel={this.state.rowModesModel}
        onRowModesModelChange={(model: GridRowModesModel) => this.setState({ rowModesModel: model })}
        processRowUpdate={this.processRowUpdate}
        onProcessRowUpdateError={(err: any) => console.error(err)}
      />
    );
  }
}

export default BudgetLineGridPanel;
